'use client';

import { Box, CloseLine, Icon } from '@viewup/ui';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AppOnlyGuideContent } from './AppOnlyGuideContent';

export interface AppOnlyGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** 제목 앞부분 — `{subject} [뷰업 앱]에서 {predicate}` (예: '이 알림은') */
  subject: string;
  /** 제목 뒷부분 (예: '확인할 수 있어요!') */
  predicate: string;
}

/**
 * 웹에서 쓸 수 없는 앱 전용 기능을 안내하는 모달(스토어 QR·바로가기).
 * context-free — 호출부가 열림 상태·onClose·제목 문구만 넘긴다.
 *
 * 구현 방식 — portal 기반 로컬 모달(전역 모달 스토어 미사용): 이 모달은 헤더 알림
 * 드롭다운 안에서도 떠야 하는데, `@viewup/ui`의 `Modal`(그리고 전역 모달 스토어가 렌더하는
 * `AlertModal`/`ConfirmModal`)은 `document.getElementById('wrapper')`가 없으면 아무것도
 * 렌더하지 않는다(`app/layout.tsx`가 실제 앱에서만 `#wrapper`를 제공). QR·스토어 링크처럼
 * 완전히 커스텀한 레이아웃이 필요해 전역 스토어의 고정 Alert/Confirm 틀에도 맞지 않는다.
 * 그래서 `document.body`로 직접 포탈하는 로컬 모달로 구현한다.
 */
export const AppOnlyGuideModal = (props: AppOnlyGuideModalProps) => {
  const { isOpen, onClose, subject, predicate } = props;
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  if (!isMounted || !isOpen) return null;

  return createPortal(
    <Box
      bgColor='bg.dimmer.60'
      // 앱 전체가 max-width 440 모바일 래퍼 안에서 렌더된다 — 헤더 알림 오버레이와 같은 폭으로 dim 한다.
      css={{ bottom: 0, left: '50%', maxWidth: 440, top: 0, transform: 'translateX(-50%)' }}
      onClick={(e) => {
        // portal 이어도 합성 이벤트는 선언 트리를 따라 NotificationItem onClick 까지 버블링된다 —
        // 막지 않으면 dim 클릭이 읽음 처리·모달 재오픈으로 이어진다.
        e.stopPropagation();
        onClose();
      }}
      position='fixed'
      width='100%'
      zIndex={2000}
    >
      <Box
        // labelledby 대신 평문 label — 제목의 강조 span 경계에서 접근 이름이 "뷰업 앱 에서"으로 끊겨 읽힌다
        aria-label={`${subject} 뷰업 앱에서 ${predicate}`}
        aria-modal='true'
        bgColor='bg.primary'
        br={8}
        css={{
          // 좁은 화면에서 maxWidth 로 줄어들면 제목이 모달 가장자리에 붙는다 — 닫기 버튼(right: 20)과 같은 거터
          boxSizing: 'border-box',
          height: 430,
          left: '50%',
          maxWidth: 'calc(100vw - 32px)',
          paddingInline: 20,
          position: 'relative',
          top: '50%',
          transform: 'translate(-50%, -50%)',
        }}
        onClick={(e) => {
          // 이 모달은 헤더 알림 드롭다운(NotificationItem) 안에서도 뜬다. React 합성 이벤트는
          // (portal의 DOM 위치가 아니라) "선언된" 컴포넌트 트리를 따라 버블링되므로, 여기서
          // 막지 않으면 닫기·스토어 링크 클릭이 NotificationItem의 onClick(읽음 처리)이나
          // 드롭다운 오버레이 닫힘까지 새어 올라간다.
          e.stopPropagation();
        }}
        role='dialog'
        width={360}
      >
        <button
          aria-label='닫기'
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            position: 'absolute',
            right: 20,
            top: 20,
          }}
          type='button'
        >
          <Icon color='text.assistance' size='S' source={CloseLine} />
        </button>

        <Box css={{ paddingTop: 64 }}>
          <AppOnlyGuideContent
            title={
              <>
                {subject}{' '}
                {/* 디자인 v2.1 강조색 — 대응하는 theme 토큰이 없다(text.point 는 노랑) */}
                <span style={{ color: '#0077FF' }}>뷰업 앱</span>
                {`에서\n${predicate}`}
              </>
            }
          />
        </Box>
      </Box>
    </Box>,
    document.body,
  );
};
